import { useSessionAudio } from '@/hooks/use-program-card-audio';
import { useUpdateCircleRecommendationStatus } from '@/hooks/use-update-circle-recommendation-status';
import { logger } from '@/libs/logger';
import { getGuideDuration, getGuideProgressDuration } from '@/utils/media';
import { DOT_STRING_SPACER } from '@/utils/string';
import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import { RbcStatus } from '@calm/evexia-web-sdk';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { useProgramItemCountText } from '@rhg/hooks/use-program-item-count-text';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import { useTrackPackItemViewedEvent } from '@rhg/hooks/use-track-pack-item-viewed-event';
import commonMessages from '@rhg/lang/messages/common';
import { useTheme } from '@rhg/libs/material';
import { CalmClinicalBadge } from '@rhg/libs/material/icons';
import { averagePackGuideMinutes } from '@rhg/utils/guides';
import {
    getProgramRoute,
    getProgramState,
    ProgramState,
} from '@rhg/utils/programs';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramContextAwareProgramCardProps } from '..';
import { styleClinicalBadgeIcon } from '../..';
import { useProgramCardPlayHandler } from '../use-program-card-play-handler';

type VariantAgnosticProgramCardProps = Pick<
    ProgramContextAwareProgramCardProps,
    | 'isTopRecommendation'
    | 'contentType'
    | 'isNotClickable'
    | 'displayGuideProgress'
    | 'trackProps'
    | 'shouldShowSessionLength'
    | 'playCurrentSessionOnClick'
>;

/**
 * Do not use this hook outside of ProgramCard and its dependencies.
 */
export function useVariantAgnosticProgramCardProps({
    isTopRecommendation,
    contentType,
    isNotClickable,
    displayGuideProgress,
    trackProps,
    shouldShowSessionLength,
    playCurrentSessionOnClick,
}: VariantAgnosticProgramCardProps) {
    const {
        program,
        recommendation,
        packClass,
        packProgram,
        progress: programProgress,
        currentSession,
        programType,
        programState,
        numSessions,
    } = useProvidedProgramContextWithGuidesAndWorkflow();

    const { updateStatusAsync } = useUpdateCircleRecommendationStatus(
        recommendation?.recDefnId
    );

    const {
        palette: { mode },
    } = useTheme();
    const isDarkMode = mode === 'dark';
    const { formatMessage } = useIntl();
    const itemCountText = useProgramItemCountText(program?.meditationType);
    const { duration, roundedDuration } = useHumanReadable();
    const { push, query } = useRouter();
    const { trackPackItemEvent } = useTrackSessionEvent();
    const packTitle = usePackContext();
    const playCurrentSession = useProgramCardPlayHandler(trackProps);

    // Surface-level analytics context (e.g. home RowIndex / RowItemIndex /
    // PackItemName / PackItemType / PackDisplayType) is supplied by the
    // composing surface via `trackProps` and forwarded into
    // `sessionTrackProps` below. This hook is surface-agnostic.

    /* Mark rec as viewed when user clicks on the card, to be used for determining plan completion metrics */
    // TODO(EVE-10506): failure logs to Datadog only; needs user-facing error UX
    const updateViewedStatus = useCallback(() => {
        updateStatusAsync({ status: RbcStatus.Viewed }).catch((err) =>
            logger.error(
                'Failed to mark recommendation as viewed',
                {},
                err as Error
            )
        );
    }, [updateStatusAsync]);

    const playHandler = useCallback(() => {
        playCurrentSession({ shouldNavigate: false }).catch((err) =>
            logger.error('Failed to play current session', {}, err as Error)
        );
    }, [playCurrentSession]);

    const detailsRoute = useMemo(() => {
        const additionalParams: Record<string, string> = {};
        if (query.category && typeof query.category === 'string') {
            additionalParams.category = query.category;
        }
        return getProgramRoute(program, packClass, 'true', additionalParams);
    }, [packClass, program, query.category]);

    const isClinicalProgram = useMemo(
        () => programType === 'clinical_program',
        [programType]
    );
    const inProgressDTC = useMemo(
        () =>
            Boolean(
                program &&
                    !program.isClinical &&
                    getProgramState(program) === ProgramState.InProgress
            ),
        [program]
    );

    const inProgressClinical = useMemo(
        () => isClinicalProgram && programState === ProgramState.InProgress,
        [isClinicalProgram, programState]
    );

    const { isActiveAudio, isPlaying } = useSessionAudio({
        inProgressClinical,
        inProgressDTC,
        currentSession,
    });

    const progressText = useCallback(
        (isChip: boolean) => {
            const numerator = currentSession?.position;
            const denominator = numSessions;
            return inProgressClinical
                ? isChip
                    ? formatMessage(commonMessages['common.of'], {
                          numerator,
                          denominator,
                      })
                    : formatMessage(commonMessages['common.session_of'], {
                          numerator,
                          denominator,
                      })
                : '';
        },
        [
            currentSession?.position,
            numSessions,
            inProgressClinical,
            formatMessage,
        ]
    );

    const currentSessionDuration = currentSession
        ? getGuideDuration(currentSession)
        : undefined;
    const currentSessionProgress = currentSession
        ? getGuideProgressDuration(currentSession)
        : undefined;

    const progressTextAlt = useMemo(() => {
        if (
            displayGuideProgress &&
            currentSessionDuration &&
            currentSessionProgress
        ) {
            return formatMessage(commonMessages['common.percent_complete'], {
                percentage: currentSessionProgress
                    ? Math.round(
                          (currentSessionProgress / currentSessionDuration) *
                              100
                      )
                    : 0,
            });
        }
    }, [
        displayGuideProgress,
        currentSessionDuration,
        currentSessionProgress,
        formatMessage,
    ]);

    // Returns persisted guide progress. This hook is called by EVERY mounted
    // card, so it must NOT read the ~4Hz `timingState` (that re-rendered all
    // cards during playback). The active banner card's LIVE progress ring is
    // handled separately in `program-context-aware` via `ActiveCardLiveProgress`,
    // which subscribes to the timing only for the one active card. EVE-12668.
    const progress = useCallback(() => {
        // DTC progress is still tracked, just never displayed — don't
        // surface a value (even an already-persisted one) here. EVE-13082.
        if (!isClinicalProgram) {
            return undefined;
        }

        // Don't show progress for the first session if it has no progress
        if (
            currentSession?.position === -1 ||
            (currentSession?.position === 1 && !currentSessionProgress)
        ) {
            return undefined;
        }

        if (
            displayGuideProgress &&
            currentSessionDuration &&
            (currentSessionProgress || currentSessionProgress === 0)
        ) {
            return currentSessionProgress / currentSessionDuration;
        }
        return programProgress;
    }, [
        isClinicalProgram,
        currentSession?.position,
        currentSessionDuration,
        currentSessionProgress,
        displayGuideProgress,
        programProgress,
    ]);

    const shouldShowAvgSessionLength = useMemo(
        () =>
            (shouldShowSessionLength || isTopRecommendation) &&
            isClinicalProgram &&
            programState !== ProgramState.InProgress,
        [
            shouldShowSessionLength,
            isTopRecommendation,
            isClinicalProgram,
            programState,
        ]
    );

    const avgDuration = useMemo(() => {
        if (shouldShowAvgSessionLength) {
            if (program?.guides?.length) {
                const { min, max } = averagePackGuideMinutes(program.guides);

                return formatMessage(
                    commonMessages['common.session_avg_length'],
                    {
                        min: roundedDuration(min),
                        max: roundedDuration(max),
                    }
                );
            }
        }
    }, [
        formatMessage,
        program?.guides,
        roundedDuration,
        shouldShowAvgSessionLength,
    ]);

    const sessionAndGuideLengthText = useMemo(
        () =>
            `${itemCountText(numSessions)}${
                avgDuration ? `${DOT_STRING_SPACER}${avgDuration}` : ''
            }`,
        [avgDuration, itemCountText, numSessions]
    );

    const categoryIcon = useMemo(
        () =>
            programType === 'clinical_program' ? (
                <CalmClinicalBadge sx={styleClinicalBadgeIcon} />
            ) : undefined,
        [programType]
    );

    const categoryTitleText = isClinicalProgram
        ? formatMessage(
              commonMessages['common.program_type.clinical_program.title']
          )
        : contentType;

    const categoryTitleColor = isClinicalProgram
        ? isDarkMode
            ? 'info.light'
            : undefined
        : 'text.sub';

    const subtitle = useMemo(() => {
        if (inProgressClinical || inProgressDTC) {
            return currentSession?.title;
        }
        return program?.narrator?.name;
    }, [
        currentSession?.title,
        inProgressClinical,
        inProgressDTC,
        program?.narrator?.name,
    ]);

    const durationText = useMemo(
        () => duration(currentSessionDuration),
        [currentSessionDuration, duration]
    );

    const label = useMemo(() => {
        if (inProgressClinical || inProgressDTC) {
            return `${subtitle} ${durationText} ${formatMessage(
                commonMessages['common.play_session']
            )}`;
        } else {
            return `${program?.title} ${formatMessage(
                commonMessages['common.view_details']
            )}`;
        }
    }, [
        inProgressClinical,
        inProgressDTC,
        subtitle,
        durationText,
        formatMessage,
        program?.title,
    ]);

    const cardClickHandler = useCallback(() => {
        if (isNotClickable) {
            return;
        }

        if (currentSession) {
            trackPackItemEvent({
                eventName: EnumProductAnalyticsEvent.PackItemClicked,
                guide: currentSession,
                program,
                packTitle,
                trackProps,
            });
        }

        if (playCurrentSessionOnClick) {
            playCurrentSession({ shouldNavigate: true }).catch((err) =>
                logger.error(
                    'Failed to navigate to program details after play',
                    {},
                    err as Error
                )
            );
            return;
        }

        if (inProgressClinical || inProgressDTC) {
            playHandler();
            return;
        }
        updateViewedStatus();

        if (detailsRoute) {
            push(detailsRoute).catch((err) =>
                logger.error(
                    'Failed to navigate to program details',
                    {},
                    err as Error
                )
            );
        }
    }, [
        isNotClickable,
        currentSession,
        trackPackItemEvent,
        program,
        packTitle,
        trackProps,
        playCurrentSessionOnClick,
        playCurrentSession,
        inProgressClinical,
        inProgressDTC,
        detailsRoute,
        playHandler,
        push,
        updateViewedStatus,
    ]);

    // Visibility tracking for "Pack: Item: Viewed" analytics event
    const cardRef = useTrackPackItemViewedEvent({
        guide: currentSession,
        program,
        trackProps,
    });

    // React 19 fix: Return undefined instead of empty string to prevent
    // "empty src attribute" error. React 19 is stricter about empty src attributes
    // and will throw an error if an empty string is passed.
    const imageSrc = useMemo(() => {
        const url =
            packProgram?.image?.url ??
            program?.image?.url ??
            program?.backgroundImage?.url;
        return url || undefined;
    }, [
        packProgram?.image?.url,
        program?.backgroundImage?.url,
        program?.image?.url,
    ]);

    return {
        programState,
        imageSrc,
        isClinicalProgram,
        inProgressClinical,
        label,
        subtitle,
        playHandler,
        program,
        detailsRoute,
        cardClickHandler,
        numSessions,
        shouldShowAvgSessionLength,
        progressText,
        inProgressDTC,
        sessionAndGuideLengthText,
        categoryTitleText,
        categoryTitleColor,
        categoryIcon,
        progress,
        durationText,
        progressTextAlt,
        isActiveAudio,
        isPlaying,
        cardRef,
        updateViewedStatus,
    };
}
